/**
 * Analytics Dashboard API Service
 *
 * Singleton client for all /api/v1/analytics endpoints + SSE real-time stream.
 * Pattern matches existing analytics.ts service style.
 */

import type {
  AnalyticsOverview,
  FunnelSummary,
  ContentPerformance,
  PlatformBreakdown,
  PublishingMetrics,
  RevenueMetrics,
  TrendsResponse,
  RealtimeData,
  AnalyticsPeriod,
  StreamEvent,
} from '../types/analytics';
import { throwIfMfaChallenge } from './mfaChallenge';

const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://claude-essay-agent-production.up.railway.app';

class DashboardApiService {
  private baseUrl: string;
  private getToken: (() => Promise<string | null>) | null = null;
  private eventSource: EventSource | null = null;

  constructor() {
    this.baseUrl = `${API_URL}/api/v1/analytics`;
  }

  /** Set auth token getter (call once on mount) */
  setTokenGetter(getter: () => Promise<string | null>) {
    this.getToken = getter;
  }

  private async headers(): Promise<Record<string, string>> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.getToken) {
      const token = await this.getToken();
      if (token) h['Authorization'] = `Bearer ${token}`;
    }
    return h;
  }

  private async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    const res = await fetch(url.toString(), { headers: await this.headers() });
    // Before the generic error: an MFA challenge must reach the router as a
    // redirect, not surface as "Analytics API error: 403".
    await throwIfMfaChallenge(res);
    if (!res.ok) throw new Error(`Analytics API error: ${res.status}`);
    return res.json();
  }

  /**
   * As `get`, but resolves to null on 403 instead of throwing.
   *
   * Some analytics endpoints report platform-wide figures that have no
   * per-account meaning - MRR and churn, the acquisition funnel, the
   * operator's own Search Console property - and the backend restricts those
   * to administrators. A merchant getting 403 there is the system working, not
   * an error, so it must not take down the panels alongside it.
   *
   * CAUTION: this is the one place where swallowing a 403 is correct, which
   * makes it the one place where swallowing an MFA challenge would be
   * invisible. MFAEnforcementMiddleware answers 403 too, and a locked-out user
   * would simply see empty admin panels and never be sent to enrollment. So
   * the MFA check runs FIRST and the `403 -> null` branch only ever sees a
   * genuine permission refusal.
   */
  private async getAdminOnly<T>(
    path: string,
    params?: Record<string, string>,
  ): Promise<T | null> {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    const res = await fetch(url.toString(), { headers: await this.headers() });
    await throwIfMfaChallenge(res);
    if (res.status === 403) return null;
    if (!res.ok) throw new Error(`Analytics API error: ${res.status}`);
    return res.json();
  }

  // --- Endpoints ---

  async getOverview(period: AnalyticsPeriod = '7d'): Promise<AnalyticsOverview> {
    return this.get('/overview', { period });
  }

  /** Platform acquisition funnel. Admin-only; null for a merchant. */
  async getFunnel(period: AnalyticsPeriod = '30d'): Promise<FunnelSummary | null> {
    return this.getAdminOnly('/funnel', { period });
  }

  async getContentPerformance(period: AnalyticsPeriod = '30d'): Promise<ContentPerformance> {
    return this.get('/content-performance', { period });
  }

  async getPlatformBreakdown(period: AnalyticsPeriod = '7d'): Promise<PlatformBreakdown> {
    return this.get('/platform-breakdown', { period });
  }

  async getPublishing(period: AnalyticsPeriod = '30d'): Promise<PublishingMetrics> {
    return this.get('/publishing', { period });
  }

  /** Platform revenue (MRR, ARR, churn, LTV). Admin-only; null for a merchant. */
  async getRevenue(): Promise<RevenueMetrics | null> {
    return this.getAdminOnly('/revenue');
  }

  async getTrends(period: AnalyticsPeriod = '30d'): Promise<TrendsResponse> {
    return this.get('/trends', { period });
  }

  async getRealtime(): Promise<RealtimeData> {
    return this.get('/realtime');
  }

  // --- SSE Real-time Stream ---

  connectRealtime(onEvent: (event: StreamEvent) => void): () => void {
    if (this.eventSource) {
      this.eventSource.close();
    }

    this.eventSource = new EventSource(`${this.baseUrl}/stream`);

    this.eventSource.onmessage = (e) => {
      try {
        const data: StreamEvent = JSON.parse(e.data);
        onEvent(data);
      } catch {
        // Ignore parse errors
      }
    };

    this.eventSource.onerror = () => {
      // Auto-reconnect is built into EventSource
    };

    // Return cleanup function
    return () => {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    };
  }

  // --- Export ---

  async exportData(format: 'csv' | 'json' = 'csv', period: AnalyticsPeriod = '30d'): Promise<Blob> {
    const url = new URL(`${this.baseUrl}/export`);
    url.searchParams.set('format', format);
    url.searchParams.set('period', period);
    const res = await fetch(url.toString(), { headers: await this.headers() });
    await throwIfMfaChallenge(res);
    if (!res.ok) throw new Error(`Export failed: ${res.status}`);
    return res.blob();
  }
}

// Singleton
const dashboardApi = new DashboardApiService();
export default dashboardApi;
